// Shared client-side validators for the charter surfaces — the FIRST validation layer (the agents
// service and the Java API are the backstops behind it). Lifted out of CharterQuoteForm so the route
// forms (RoutePopup / RouteFormMulti / RouteFormRoundTrip) and the Ops forms validate IDENTICALLY
// instead of each re-implementing — or silently skipping — the same checks.
//
// Kept in lockstep with the Python agent's per-field contact validation (one_way._phone_ok /
// _email_ok) and the planner's slot rules. Every helper is pure so it's unit-testable.
//
// Last verified: 2026-07-18 (peoria)

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM',
  'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA',
  'WV', 'WI', 'WY',
];
const US_STATE_SET = new Set(US_STATES);

// Contact validators — phone is 10 digits (or 11 with a leading US "1"); email is local@domain.tld.
export const isValidPhone = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
};
export const isValidEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || '').trim());

// A 2-letter US state code. EMPTY is allowed — a city-only endpoint is valid for a preliminary
// estimate — but a NON-empty value must be a real code.
export const isValidState = (v) => {
  const s = String(v || '').trim().toUpperCase();
  return s === '' || US_STATE_SET.has(s);
};
// True only when a state was actually typed AND it isn't a real code — drives the inline error/border.
export const stateInvalid = (v) => String(v || '').trim() !== '' && !isValidState(v);

// today's date (local) as YYYY-MM-DD — for date pickers' future-only `min` and date-only past checks.
export const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
// now (local) as YYYY-MM-DDTHH:mm — for a datetime-local picker's `min`.
export const nowLocalISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// "YYYY-MM-DD HH:mm" | "YYYY-MM-DDTHH:mm[:ss]" | "YYYY-MM-DD" -> epoch ms (missing time = start of
// day). NaN when unparseable, so a bad value is left to the required/format checks, not flagged here.
const toMs = (v) => {
  const s = String(v || '').trim().replace(' ', 'T');
  if (!s) return NaN;
  const withTime = s.length <= 10 ? `${s}T00:00:00` : (s.length === 16 ? `${s}:00` : s);
  return Date.parse(withTime);
};

// A datetime is in the past when it's before now. Date-only values compare by calendar day (so a
// booking today with no time yet is NOT rejected); datetimes compare to the current instant.
export const isPastDateTime = (v) => {
  const s = String(v || '').trim().replace(' ', 'T');
  if (!s) return false;
  if (s.length <= 10) return s < todayISO();     // "2026-07-10" < "2026-07-18"
  const ms = toMs(s);
  return Number.isFinite(ms) && ms < Date.now();
};

// The return leg must not depart before the outbound leg. Compares combined datetimes (missing time =
// start of day) so a same-day round trip is allowed and only a genuinely-earlier return is rejected.
export const returnBeforeOutbound = (outboundDT, returnDT) => {
  const o = toMs(outboundDT);
  const r = toMs(returnDT);
  return Number.isFinite(o) && Number.isFinite(r) && r < o;
};

// A normalized identity for a stop, for dedupe: venue name + city + state (or the resolved address
// label when that's all we have), lowercased with punctuation/whitespace collapsed. Two stops with the
// same identity are the same stop ("DeKalb, IL" == "dekalb il").
export const stopKey = (stop) => {
  if (!stop) return '';
  const parts = [stop.name, stop.city, stop.state].filter(Boolean).join(' ') || String(stop.label || '');
  return parts.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
};
// The set of indices of stops that repeat an EARLIER stop — so the form can flag them inline.
export const duplicateStopIndices = (stops) => {
  const seen = new Set();
  const dup = new Set();
  (stops || []).forEach((s, i) => {
    const k = stopKey(s);
    if (!k) return;                   // blank/unpicked slot — not a duplicate
    if (seen.has(k)) dup.add(i);
    else seen.add(k);
  });
  return dup;
};
// Drop repeated stops, keeping the first occurrence — used at token build so a duplicate never reaches
// the agents. Blank (unpicked) slots pass through for the caller's existing .filter(Boolean).
export const dedupeStops = (stops) => {
  const seen = new Set();
  return (stops || []).filter((s) => {
    const k = stopKey(s);
    if (!k) return true;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};
// True when no stop repeats another — the boolean form for a `canConfirm` gate.
export const stopsAreDistinct = (stops) => duplicateStopIndices(stops).size === 0;
